/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { trans } from '@spotahome/soyuz/client';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';

import useHorizontalDrag from '../../../hooks/useHorizontalDrag';

import { useSectionNavigation } from '../../context/SectionNavigationContext';

import NavigationAngle from './NavigationAngle';

// Keeping as simple SCSS, as there are constrains on using CSS Modules in this component due to CSS inheritance
import './SectionContentNavigation.scss';

export const SectionContentNavigation = ({
  items = [],
  className = null,
  containerClassName = null,
  itemClassName = null,
  selectedItemClassName = null,
  eventPrefix,
  showArrows = true
}) => {
  const [selectedItem, setSelectedItem] = useState(0);
  const {
    containerRef,
    isDragging,
    handleMouseDown,
    handleMouseUp,
    handleTouchStart,
    handleTouchEnd
  } = useHorizontalDrag({
    onMoveStart: () => {},
    onMove: () => {},
    itemsOnLeftClassName: 'section-content-navigation__list--items-on-left',
    itemsOnRightClassName: 'section-content-navigation__list--items-on-right'
  });

  const wrapperRef = useRef();
  const { sectionInView, scrollToSection, setHeight, navigationRef } =
    useSectionNavigation();

  const handleNavClick = side => () => {
    const offset = side === 'left' ? -1 : 1;

    const current = items[selectedItem];
    const prevItem = items[selectedItem + offset];
    const { section } = prevItem ? prevItem.data : current.data;

    SoyuzAnalytics.sendGA4Event(`${eventPrefix}-section-arrow-selected`, {
      section
    });

    scrollToSection(section);
  };

  const handleItemClick = section => e => {
    e.preventDefault();
    e.stopPropagation();
    if (isDragging()) {
      return;
    }

    SoyuzAnalytics.sendGA4Event(`${eventPrefix}-section-selected`, {
      section
    });
    scrollToSection(section);
  };

  const resizeHandler = () => {
    if (navigationRef.current) {
      const { height: navHeight } =
        navigationRef.current.getBoundingClientRect();
      setHeight(navHeight);
    }
  };

  useEffect(() => {
    if (selectedItem !== sectionInView) {
      const next = items.findIndex(item => item.data.section === sectionInView);

      if (next >= 0) {
        setSelectedItem(next);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionInView]);

  useEffect(() => {
    window.addEventListener('resize', resizeHandler);
    resizeHandler();
    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, []);

  const wrapperClassName = classNames('section-content-navigation', className);

  return (
    <div className={wrapperClassName} ref={navigationRef}>
      <div
        className={classNames(
          'section-content-navigation__wrapper',
          containerClassName
        )}
      >
        <div
          ref={wrapperRef}
          className="section-content-navigation__inner-wrapper"
        >
          <ul
            ref={containerRef}
            className="section-content-navigation__list"
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {items.map((item, index) => {
              const { section, textKey } = item.data;
              return (
                // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                <li
                  ref={item.ref}
                  key={section}
                  className={classNames(
                    'section-content-navigation__list-item',
                    itemClassName,
                    {
                      [selectedItemClassName]: index === selectedItem,
                      'section-content-navigation__list-item--active':
                        index === selectedItem
                    }
                  )}
                  onClick={handleItemClick(section)}
                  // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                  tabIndex="0"
                >
                  {trans(textKey)}
                </li>
              );
            })}
          </ul>
          {showArrows && (
            <>
              <NavigationAngle
                side="left"
                onClick={handleNavClick('left')}
                className="section-content-navigation__arrow section-content-navigation__arrow--left"
              />

              <NavigationAngle
                side="right"
                onClick={handleNavClick('right')}
                className="section-content-navigation__arrow section-content-navigation__arrow--right"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

SectionContentNavigation.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      data: PropTypes.shape({
        section: PropTypes.string.isRequired,
        textKey: PropTypes.string.isRequired
      }).isRequired,
      ref: PropTypes.object.isRequired
    })
  ).isRequired,
  className: PropTypes.string,
  containerClassName: PropTypes.string,
  itemClassName: PropTypes.string,
  selectedItemClassName: PropTypes.string,
  eventPrefix: PropTypes.string.isRequired,
  showArrows: PropTypes.bool
};

export default SectionContentNavigation;
